import {
  GOVERNANCE_ACTIONS_FILTERS,
  PPU_ECONOMIC_GROUP_PARAMS_KEYS,
  PPU_GOVERNANCE_GROUP_PARAMS_KEYS,
  PPU_NETWORK_GROUP_PARAMS_KEYS,
  PPU_TECHNICAL_GROUP_PARAMS_KEYS,
} from '@src/constants';
import type { IVoteResponse } from '@src/models/dtos';
import type {
  EpochParams,
  GetGovActionVotingThresholdKeyOptions,
  IGovernanceAction,
  IProposalVoterType,
  IVotePowerCount,
  VotedType,
  Voter,
} from '@src/models/types';
import { SECURITY_RELEVANT_PARAMS_MAP } from '@src/constants';
import { GovernanceActionType } from '@src/models/enum';
import environments from '@src/configs/environments';
import type {
  MetadataValidationRequest,
  metadataValidationStatus,
} from '@src/models/dtos/metadata';
import type { ProposalType } from '@src/models/types';

export function calculateThreshold(
  voteResponse: IVoteResponse,
  currentVote: number
) {
  const totalVotes =
    voteResponse.Yes +
    voteResponse.No +
    voteResponse.Abstained +
    voteResponse['Not Voted'];
  if (totalVotes === 0) return 0;
  return parseFloat(((currentVote / totalVotes) * 100).toFixed(2));
}

export function getTextAndBackgroundColor(type: VotedType) {
  switch (type) {
    case 'Yes':
      return {
        textColor: 'text-semantic-success-500',
        backgroundColor: 'bg-semantic-success-500',
      };
    case 'No':
      return {
        textColor: 'text-semantic-error-500',
        backgroundColor: 'bg-semantic-error-500',
      };
    case 'Abstained':
      return {
        textColor: 'text-blue-500',
        backgroundColor: 'bg-blue-500',
      };

    default:
      return {
        textColor: 'text-gray-400',
        backgroundColor: 'bg-gray-400',
      };
  }
}

function getExtractedVote(vote: IVotePowerCount | number) {
  if (typeof vote === 'number') {
    return vote;
  }
  return parseInt(vote.power || '0');
}

export function getVoteResponse(
  voter: Voter,
  vote: IProposalVoterType
): IVoteResponse {
  switch (voter) {
    case 'DReps':
      return {
        Yes: getExtractedVote(vote.drep.yes),
        No: getExtractedVote(vote.drep.no),
        Abstained:
          getExtractedVote(vote.drep.abstain) +
          (vote.drep.delegators
            ? getExtractedVote(vote.drep.delegators.abstain)
            : 0),
        'Not Voted': getExtractedVote(vote.drep.notVoted),
      };
    case 'SPOs':
      return {
        Yes: getExtractedVote(vote.spo.yes),
        No: getExtractedVote(vote.spo.no),
        Abstained: getExtractedVote(vote.spo.abstain),
        'Not Voted': getExtractedVote(vote.spo.notVoted),
      };
    default:
      return {
        Yes: getExtractedVote(vote.cc.yes),
        No: getExtractedVote(vote.cc.no),
        Abstained: getExtractedVote(vote.cc.abstain),
        'Not Voted': getExtractedVote(vote.cc.notVoted),
      };
  }
}

export const getProposalTypeLabel = (type: string) => {
  const label = GOVERNANCE_ACTIONS_FILTERS.find((i) => i.key === type)?.label;
  return label || type;
};

export const areSPOVoteTotalsDisplayed = (
  governanceAction: IGovernanceAction
) => {
  const { proposal, meta } = governanceAction;
  const { protocolParams } = meta;
  const isSecurityGroup = Object.values(SECURITY_RELEVANT_PARAMS_MAP).some(
    (paramKey) =>
      protocolParams?.[paramKey as keyof typeof protocolParams] !== null
  );
  return !(
    proposal.type === GovernanceActionType.UpdateToTheConstitution ||
    proposal.type === GovernanceActionType.TreasuryWithdrawal ||
    (proposal.type === GovernanceActionType.ProtocolParameterChange &&
      !isSecurityGroup)
  );
};

export const areCCVoteTotalsDisplayed = (
  governanceActionType: GovernanceActionType
) => {
  return ![
    GovernanceActionType.NoConfidence,
    GovernanceActionType.NewCommittee,
  ].includes(governanceActionType);
};

const LOVELACE = 1000000;
export const correctVoteAdaFormat = (
  lovelace: number | undefined,
  locale: string | undefined = undefined
) => {
  if (lovelace) {
    const ada = lovelace / LOVELACE;

    return ada.toLocaleString(locale, {
      maximumFractionDigits: 3,
    });
  }
  return '0';
};

export async function getProposalById(
  id: string
): Promise<IGovernanceAction | null> {
  const url = environments.INTERNAL_API_URL + `/proposal/${id}?vote_count=true`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response.status === 200) return await response.json();
    console.error('error in fetching proposal', response);
    return null;
  } catch (e) {
    console.error('error in fetching proposal', e);
    return null;
  }
}

export function extractIndex(url: string) {
  const hashIndex = url.indexOf('#');
  if (hashIndex === -1) return null;

  let endIndex = url.indexOf('?', hashIndex);
  if (endIndex === -1) endIndex = url.length;

  return url.slice(hashIndex + 1, endIndex);
}

export function invalidMetadataInfo(status: metadataValidationStatus) {
  switch (status) {
    case 'URL_NOT_FOUND':
      return {
        title: 'Data Missing',
        subtitle:
          'The data that was originally used when this Governance Action was created has not been found.',
        description:
          'DrepID uses external sources for Governance Action data, and these sources are maintained by the proposers of the Actions. This error means that DrepID cannot locate the data on the URL specified when the Governance Action was originally posted.',
      };
    case 'INVALID_HASH':
      return {
        title: 'Data Invalid',
        subtitle:
          'The data at the URL registered for this Governance Action does not match what was registered on-chain.',
        description:
          'DrepID uses external sources for Governance Action data, and these sources are maintained by the proposers of the Actions. The document was found, but its hash differs from the one recorded on-chain — so it has been changed, replaced, or served incorrectly since the Governance Action was posted, and it cannot be trusted to be what was voted on.',
      };
    case 'INCORRECT_FORMAT':
      return {
        title: 'Data Formatted Incorrectly',
        subtitle:
          'The data that was originally used when this DRep was created has been formatted incorrectly.',
        description:
          'DrepID uses external sources for DRep data, and these sources are maintianed by the DReps themselves. This error means that the data stored by the DRep does not match the format defined by the DRep spec.',
      };
    default:
      return {
        title: 'Data not Verifiable',
        subtitle:
          'The data that was originally used when this Governance Action was created has changed.',
        description:
          'DrepID uses external sources for Governance Action data, and these sources are maintained by the proposers of the Actions. This error means that the data stored by the Proposer does not match the data when the Governance Action was originally posted.',
      };
  }
}

export const fetchProposalMetadata = async (proposal: ProposalType) => {
  if (proposal.metadataUrl && proposal.metadataHash) {
    const metadataValidationRequest: MetadataValidationRequest = {
      hash: proposal.metadataHash,
      url: proposal.metadataUrl,
      standard: 'CIP108',
    };
    try {
      const response = await fetch('/metadata-validation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(metadataValidationRequest),
      });
      const metadataValidationResponse = await response.json();
      return metadataValidationResponse;
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    }
  }
  return null;
};

const getParameterChangeThreshold = (
  protocolParams?: Partial<EpochParams> | null,
  voterType?: Voter
): VotingThresholdKey => {
  if (!protocolParams || voterType !== 'DReps') return undefined;

  const protocolParamKeys = Object.keys(protocolParams).filter((key) =>
    ['number', 'string'].includes(
      typeof protocolParams[key as keyof EpochParams]
    )
  );

  if (
    PPU_ECONOMIC_GROUP_PARAMS_KEYS.some((key) =>
      protocolParamKeys.includes(key)
    )
  ) {
    return 'dvt_p_p_economic_group';
  }
  if (
    PPU_GOVERNANCE_GROUP_PARAMS_KEYS.some((key) =>
      protocolParamKeys.includes(key)
    )
  ) {
    return 'dvt_p_p_gov_group';
  }
  if (
    PPU_NETWORK_GROUP_PARAMS_KEYS.some((key) => protocolParamKeys.includes(key))
  ) {
    return voterType === 'DReps'
      ? 'dvt_p_p_network_group'
      : 'pvtpp_security_group';
  }
  if (
    PPU_TECHNICAL_GROUP_PARAMS_KEYS.some((key) =>
      protocolParamKeys.includes(key)
    )
  ) {
    return 'dvt_p_p_technical_group';
  }
  return undefined;
};

type VotingThresholdKey = keyof EpochParams | undefined;
export const getGovActionVotingThresholdKey = ({
  govActionType,
  protocolParams,
  voterType,
}: GetGovActionVotingThresholdKeyOptions): VotingThresholdKey => {
  const govActionThresholds: Record<GovernanceActionType, VotingThresholdKey> =
    {
      [GovernanceActionType.NewCommittee]:
        voterType === 'DReps'
          ? 'dvt_committee_normal'
          : voterType === 'SPOs'
            ? 'pvt_committee_normal'
            : undefined,
      [GovernanceActionType.HardFork]:
        voterType === 'DReps'
          ? 'dvt_hard_fork_initiation'
          : voterType === 'SPOs'
            ? 'pvt_hard_fork_initiation'
            : undefined,
      [GovernanceActionType.ProtocolParameterChange]:
        getParameterChangeThreshold(protocolParams, voterType),
      [GovernanceActionType.TreasuryWithdrawal]:
        voterType === 'DReps' ? 'dvt_treasury_withdrawal' : undefined,
      [GovernanceActionType.UpdateToTheConstitution]:
        voterType === 'DReps' ? 'dvt_update_to_constitution' : undefined,
      [GovernanceActionType.NoConfidence]:
        voterType === 'DReps'
          ? 'dvt_motion_no_confidence'
          : 'pvt_motion_no_confidence',
      [GovernanceActionType.InfoAction]: undefined,
    };

  return govActionThresholds[govActionType] ?? undefined;
};
